import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/components/ui/sheet";
import {
  ArrowDownToLine, ArrowUpFromLine, CheckCircle2, Clock,
  XCircle, Loader2, Copy, RefreshCw, Send,
} from "lucide-react";
import { format } from "date-fns";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

/* ── Design Tokens ── */
const C = {
  bg: "#060C18", card: "#0E1C35", cardHigh: "#122040",
  border: "rgba(255,255,255,0.055)", borderMid: "rgba(255,255,255,0.09)",
  cyan: "#22D3EE", cyanSoft: "rgba(34,211,238,0.08)", cyanGlow: "rgba(34,211,238,0.18)",
  blue: "#3B82F6", blueSoft: "rgba(59,130,246,0.10)",
  emerald: "#10B981", emeraldSoft: "rgba(16,185,129,0.10)",
  amber: "#FBBF24", amberSoft: "rgba(251,191,36,0.08)",
  rose: "#FB7185", roseSoft: "rgba(251,113,133,0.10)",
  t1: "#EFF6FF", t2: "#93B4D4", t3: "#4D6E90",
};

const TD_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');

  .td-sheet [data-radix-scroll-area-viewport],
  .td-sheet { background: #060C18 !important; border-left: 1px solid rgba(255,255,255,0.07) !important; }
  .td-sheet * { font-family: 'Outfit', system-ui, sans-serif; }

  .td-sheet ::-webkit-scrollbar { width: 5px; }
  .td-sheet ::-webkit-scrollbar-track { background: transparent; }
  .td-sheet ::-webkit-scrollbar-thumb { background: rgba(34,211,238,0.2); border-radius: 99px; }

  .td-header {
    padding: 1.25rem 1.25rem 0.875rem;
    border-bottom: 1px solid rgba(255,255,255,0.055);
    background: rgba(34,211,238,0.02);
  }

  .td-body { background: #060C18; padding: 0 1.25rem 2rem; }

  .td-type-icon {
    width: 2.75rem; height: 2.75rem; border-radius: 0.875rem;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }

  .td-title { font-size: 1rem; font-weight: 700; color: #EFF6FF; margin: 0; }
  .td-subtitle { font-family: ui-monospace, monospace; font-size: 0.72rem; color: #4D6E90; margin-top: 0.25rem; }

  /* Status banner */
  .td-status-banner {
    border-radius: 0.875rem; padding: 1rem 1.25rem;
    margin: 1.25rem 0;
    display: flex; align-items: center; justify-content: space-between;
  }
  .td-status-left { display: flex; align-items: center; gap: 0.5rem; }
  .td-status-label { font-size: 0.9rem; font-weight: 700; }
  .td-amount { font-size: 1.75rem; font-weight: 800; letter-spacing: -0.03em; }
  .td-error-msg { font-size: 0.78rem; margin-top: 0.5rem; opacity: 0.85; }

  /* Section title */
  .td-section-title {
    font-size: 0.68rem; font-weight: 800; color: #4D6E90;
    text-transform: uppercase; letter-spacing: 0.1em;
    margin: 1.25rem 0 0.5rem;
    display: flex; align-items: center; gap: 0.5rem;
  }
  .td-section-title::after { content: ''; flex: 1; height: 1px; background: rgba(255,255,255,0.055); }

  /* Info card */
  .td-info-card {
    background: #0E1C35;
    border: 1px solid rgba(255,255,255,0.055);
    border-radius: 0.75rem; overflow: hidden;
    margin-bottom: 0.75rem;
    transition: border-color 0.2s;
  }
  .td-info-card:hover { border-color: rgba(34,211,238,0.2); }

  /* Info row */
  .td-info-row {
    display: flex; align-items: flex-start; justify-content: space-between;
    padding: 0.6rem 0.875rem;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    transition: background 0.15s;
  }
  .td-info-row:last-child { border-bottom: none; }
  .td-info-row:hover { background: rgba(34,211,238,0.03); }
  .td-row-label { font-size: 0.74rem; color: #4D6E90; font-weight: 500; padding-right: 1rem; flex-shrink: 0; white-space: nowrap; }
  .td-row-value { font-size: 0.78rem; color: #93B4D4; text-align: right; word-break: break-all; }
  .td-row-value.mono { font-family: ui-monospace, monospace; color: #22D3EE; }

  /* Copy btn */
  .td-copy-btn {
    background: none; border: none; cursor: pointer;
    padding: 0.125rem; border-radius: 0.25rem;
    display: flex; align-items: center;
    transition: background 0.15s; flex-shrink: 0; margin-left: 4px;
  }
  .td-copy-btn:hover { background: rgba(34,211,238,0.12); }
  .td-copy-btn svg { width: 0.7rem; height: 0.7rem; color: #4D6E90; transition: color 0.15s; }
  .td-copy-btn:hover svg { color: #22D3EE; }

  /* Action buttons */
  .td-actions { display: flex; gap: 0.75rem; margin-top: 1.5rem; padding-top: 1.5rem; border-top: 1px solid rgba(255,255,255,0.055); }

  .td-btn-primary {
    flex: 1; display: flex; align-items: center; justify-content: center; gap: 0.5rem;
    padding: 0.55rem 1rem; border-radius: 0.625rem;
    border: 1px solid rgba(34,211,238,0.3); background: rgba(34,211,238,0.08);
    color: #22D3EE; font-size: 0.8375rem; font-weight: 700; cursor: pointer;
    transition: all 0.2s;
  }
  .td-btn-primary:hover { background: rgba(34,211,238,0.15); border-color: rgba(34,211,238,0.5); box-shadow: 0 0 14px rgba(34,211,238,0.2); }

  .td-btn-outline {
    flex: 1; display: flex; align-items: center; justify-content: center; gap: 0.5rem;
    padding: 0.55rem 1rem; border-radius: 0.625rem;
    border: 1px solid rgba(255,255,255,0.09); background: #0E1C35;
    color: #93B4D4; font-size: 0.8375rem; font-weight: 700; cursor: pointer;
    transition: all 0.2s;
  }
  .td-btn-outline:hover { background: rgba(34,211,238,0.08); border-color: rgba(34,211,238,0.3); color: #22D3EE; }
`;

const statusConfig = {
  success: { bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.25)", text: "#34D399", amountColor: "#34D399", icon: CheckCircle2 },
  pending: { bg: "rgba(251,191,36,0.10)", border: "rgba(251,191,36,0.25)", text: "#FBBF24", amountColor: "#22D3EE", icon: Clock },
  processing: { bg: "rgba(59,130,246,0.10)", border: "rgba(59,130,246,0.25)", text: "#60A5FA", amountColor: "#22D3EE", icon: Loader2 },
  failed: { bg: "rgba(251,113,133,0.10)", border: "rgba(251,113,133,0.25)", text: "#FB7185", amountColor: "#FB7185", icon: XCircle },
  expired: { bg: "rgba(71,85,105,0.15)", border: "rgba(71,85,105,0.28)", text: "#64748B", amountColor: "#64748B", icon: Clock },
  cancelled: { bg: "rgba(71,85,105,0.15)", border: "rgba(71,85,105,0.28)", text: "#64748B", amountColor: "#64748B", icon: XCircle },
  declined: { bg: "rgba(251,113,133,0.10)", border: "rgba(251,113,133,0.25)", text: "#FB7185", amountColor: "#FB7185", icon: XCircle },
};

const getStatusConf = (t) => {
  const labels = {
    success: t?.("success") || "Success",
    pending: t?.("pending") || "Pending",
    processing: t?.("processing") || "Processing",
    failed: t?.("failed") || "Failed",
    expired: t?.("expired") || "Expired",
    cancelled: t?.("cancelled") || "Cancelled",
    declined: t?.("declined") || "Declined",
  };
  return Object.fromEntries(Object.entries(statusConfig).map(([k, v]) => [k, { ...v, label: labels[k] }]));
};

export default function TransactionDetail({ transaction, open, onClose, onRetry, onResendCallback, isQRCode = false }) {
  const { t } = useLanguage();
  if (!transaction) return null;

  const cfg = getStatusConf(t);
  const statusKey = (transaction?.status || "").toLowerCase();
  const status = cfg[statusKey] || cfg.pending;
  const StatusIcon = status.icon;
  const isWithdrawal = transaction.type === "WITHDRAWAL";

  const createdAtValue =
    transaction.created_date ||
    transaction.createdAt ||
    transaction._raw?.createdAt ||
    null;

  const updatedAtValue =
    transaction.updated_date ||
    transaction.updatedAt ||
    transaction.processed_at ||
    transaction.completed_at ||
    transaction._raw?.updatedAt ||
    null;

  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  const InfoRow = ({ label, value, copyable = false, mono = false }) => (
    <div className="td-info-row">
      <span className="td-row-label">{label}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span className={`td-row-value ${mono ? "mono" : ""}`}>{value || "N/A"}</span>
        {copyable && value && (
          <button className="td-copy-btn" onClick={() => copyToClipboard(value)}>
            <Copy />
          </button>
        )}
      </div>
    </div>
  );

  const SectionTitle = ({ label }) => (
    <p className="td-section-title">{label}</p>
  );

  return (
    <>
      <style>{TD_CSS}</style>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent className="td-sheet w-full sm:max-w-lg overflow-y-auto" style={{ background: C.bg, border: "none" }}>

          {/* Header */}
          <SheetHeader className="td-header">
            <SheetTitle style={{ display: "flex", alignItems: "center", gap: "0.875rem" }}>
              <div className="td-type-icon" style={{
                background: isWithdrawal ? "rgba(251,191,36,0.10)" : "rgba(59,130,246,0.10)",
                border: `1px solid ${isWithdrawal ? "rgba(251,191,36,0.25)" : "rgba(59,130,246,0.25)"}`,
              }}>
                {isWithdrawal
                  ? <ArrowUpFromLine style={{ width: "1.2rem", height: "1.2rem", color: C.amber }} />
                  : <ArrowDownToLine style={{ width: "1.2rem", height: "1.2rem", color: C.blue }} />}
              </div>
              <div>
                <p className="td-title">
                  {isQRCode ? (t("qrCodeDetails") || "QR Code Details") : t("transactionDetails")}
                </p>
                <p className="td-subtitle">
                  {isQRCode ? transaction.payment_id : transaction.transaction_id}
                </p>
              </div>
            </SheetTitle>
          </SheetHeader>

          <div className="td-body" style={{ paddingTop: "1rem" }}>

            {/* Status Banner */}
            <div className="td-status-banner" style={{ background: status.bg, border: `1px solid ${status.border}` }}>
              <div className="td-status-left">
                <StatusIcon style={{
                  width: "1.1rem", height: "1.1rem", color: status.text,
                  animation: statusKey === "processing" ? "spin 1s linear infinite" : "none",
                }} />
                <span className="td-status-label" style={{ color: status.text }}>{status.label}</span>
              </div>
            </div>

            {!isQRCode && transaction.error_message && (
              <p className="td-error-msg" style={{ color: status.text, padding: "0 0 8px" }}>{transaction.error_message}</p>
            )}

            {/* QR Code Info */}
            {isQRCode ? (
              <>
                <SectionTitle label={t("qrCodeInfo") || "QR Code Info"} />
                <div className="td-info-card">
                  <InfoRow label={t("paymentId") || "Payment ID"} value={transaction.payment_id} copyable mono />
                  {transaction.reference && <InfoRow label={t("reference")} value={transaction.reference} copyable mono />}
                  <InfoRow label={t("currency")} value={transaction.currency || "THB"} />
                  <InfoRow label={t("grossAmount" || "Gross Amount")} value={`฿${transaction.amount?.toLocaleString() || 0}`} />
                  <InfoRow label={t("netAmount")} value={`฿${transaction.net_amount?.toLocaleString() || 0}`} />
                  <InfoRow label={t("totalFee")} value={`฿${transaction.total_fee?.toLocaleString() || 0}`} />
                  <InfoRow label={t("status")} value={status.label} />
                </div>

                <SectionTitle label={t("processingInfo")} />
                <div className="td-info-card">
                  <InfoRow label={t("merchant")} value={transaction.merchant?.name || transaction.merchant?.code || ""} />
                  <InfoRow label={t("provider")} value={transaction.client_bank_name || ""} />
                  {transaction.client_name && <InfoRow label={t("clientName") || "Client Name"} value={transaction.client_name} />}
                  {transaction.client_bank_account && (
                    <InfoRow label={t("clientBankAccount") || "Client Bank Account"} value={transaction.client_bank_account} copyable mono />
                  )}
                </div>

                {transaction.expires_at && (
                  <>
                    <SectionTitle label={t("expiryInfo") || "Expiry Info"} />
                    <div className="td-info-card">
                      <InfoRow label={t("expiresAt") || "Expires At"} value={format(new Date(transaction.expires_at), "MMM d, yyyy HH:mm:ss")} />
                    </div>
                  </>
                )}

                <SectionTitle label={t("timestamps")} />
                <div className="td-info-card">
                  <InfoRow
                    label={t("createdAt")}
                    value={createdAtValue ? format(new Date(createdAtValue), "MMM d, yyyy HH:mm:ss") : "N/A"}
                  />
                  <InfoRow
                    label={t("updatedAt")}
                    value={updatedAtValue ? format(new Date(updatedAtValue), "MMM d, yyyy HH:mm:ss") : "N/A"}
                  />
                </div>
              </>
            ) : (
              <>
                <SectionTitle label={t("transactionInfo")} />
                <div className="td-info-card">
                  <InfoRow label={t("transactionId")} value={transaction.transaction_id} copyable mono />
                  <InfoRow label={t("type")} value={isWithdrawal ? t("withdrawal") : t("deposit")} />
                  <InfoRow label={t("currency")} value={transaction.currency || "THB"} />
                  <InfoRow label={t("grossAmount")} value={`฿${transaction.amount || 0}`} />
                  <InfoRow label={t("feeAmount")} value={`฿${transaction.total_fee || 0}`} />
                  <InfoRow label={t("netAmount")} value={`฿${transaction.net_amount || 0}`} />
                </div>

                <SectionTitle label={t("processingInfo")} />
                <div className="td-info-card">
                  <InfoRow label={t("merchant")} value={transaction.merchant?.name} />
                  <InfoRow label={t("provider")} value={transaction.bank_code} />
                </div>

                <SectionTitle label={t("customerInfo")} />
                <div className="td-info-card">
                  <InfoRow label={t("name")} value={transaction.customer_name} />
                </div>

                {isWithdrawal && (
                  <>
                    <SectionTitle label={t("bankDetails")} />
                    <div className="td-info-card">
                      <InfoRow label={t("bank")} value={transaction.client_bank_name} />
                      <InfoRow label={t("account")} value={transaction.bank_account} />
                    </div>
                  </>
                )}

                <SectionTitle label={t("timestamps")} />
                <div className="td-info-card">
                  <InfoRow label={t("createdAt")} value={transaction.created_date ? format(new Date(transaction.created_date), "MMM d, yyyy HH:mm:ss") : "N/A"} />
                </div>

                {/* Actions */}
                {((transaction.status === "failed" || transaction.status === "expired") || transaction.callback_status === "failed") && (
                  <div className="td-actions">
                    {(transaction.status === "failed" || transaction.status === "expired") && (
                      <button className="td-btn-primary" onClick={() => onRetry?.(transaction)}>
                        <RefreshCw style={{ width: "0.875rem", height: "0.875rem" }} />
                        {t("retryTransaction")}
                      </button>
                    )}
                    {transaction.callback_status === "failed" && (
                      <button className="td-btn-outline" onClick={() => onResendCallback?.(transaction)}>
                        <Send style={{ width: "0.875rem", height: "0.875rem" }} />
                        {t("resendCallback")}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}